import { Project } from "@/models/project.model"
import { computed, inject, type Ref, ref, watch } from "vue"
import { KnownBusinessEntitySaleItemController } from "@/controllers/model-controllers/known-business-entity-sale-item-controller.ts"
import { ProjectSaleItemController } from "@/controllers/model-controllers/project-sale-item-controller"
import { ProjectPurchaseItemController } from "@/controllers/model-controllers/project-purchase-item-controller"
import type { ProjectRentalIntakeItemController } from "@/controllers/model-controllers/project-rental-intake-item-controller"
import type { ProjectRentalDispenseItemController } from "@/controllers/model-controllers/project-rental-dispense-item-controller"
import type { KnownBusinessEntityRentalDispenseItemController } from "@/controllers/model-controllers/known-business-entity-rental-dispense-item-controller.ts"
import { isEqual } from "lodash-es"

export default function useAutomaticPriceAgreementRetrieval(
  project: Ref<Project | undefined>,
  retrieveRentalPriceAgreements = false
) {
  const projectPurchaseItemController: ProjectPurchaseItemController = inject(
    "projectPurchaseItemController"
  )!
  const projectSaleItemController: ProjectSaleItemController = inject("projectSaleItemController")!
  const projectRentalIntakeItemController: ProjectRentalIntakeItemController = inject(
    "projectRentalIntakeItemController"
  )!

  const projectRentalDispenseItemController: ProjectRentalDispenseItemController = inject(
    "projectRentalDispenseItemController"
  )!
  const knownBusinessEntitySaleItemController: KnownBusinessEntitySaleItemController = inject(
    "knownBusinessEntitySaleItemController"
  )!
  const knownBusinessEntityRentalDispenseItemController: KnownBusinessEntityRentalDispenseItemController =
    inject("knownBusinessEntityRentalDispenseItemController")!

  const isRetrievingCount = ref(0)
  const isRetrieving = computed(() => {
    return isRetrievingCount.value > 0
  })

  // Whenever the project or the known business entity of the project changes, retrieve the list of price agreements.
  watch(
    () => [project.value?.id, project.value?.executor.knownBusinessEntity?.id],
    (newVal, oldVal) => {
      if (!isEqual(newVal, oldVal)) {
        retrieve()
      }
    }
  )

  function retrieve() {
    if (project.value) {
      // Retrieve the list of price agreements for the project.
      const promises = [
        projectPurchaseItemController.getListForProject(project.value),
        projectSaleItemController.getListForProject(project.value)
      ]

      if (retrieveRentalPriceAgreements) {
        promises.push(
          projectRentalIntakeItemController.getListForProject(project.value),
          projectRentalDispenseItemController.getListForProject(project.value)
        )
      }

      // If the project has a known business entity, retrieve the list of price agreements for the known business entity.
      if (project.value.executor?.knownBusinessEntity) {
        promises.push(
          knownBusinessEntitySaleItemController.getListForKnownBusinessEntity(
            project.value.executor.knownBusinessEntity
          )
        )
        if (retrieveRentalPriceAgreements) {
          promises.push(
            knownBusinessEntityRentalDispenseItemController.getListForKnownBusinessEntity(
              project.value.executor.knownBusinessEntity
            )
          )
        }
      }

      isRetrievingCount.value++
      Promise.all(promises).then(() => {
        isRetrievingCount.value--
      })
    }
  }

  return {
    isRetrieving,
    retrieve
  }
}
